import { useTranslation } from '@multaqa/i18n'
import { Button } from '@multaqa/ui/components/ui/button'
import { SegmentedControl } from '@multaqa/ui/components/ui/segmented-control'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@multaqa/ui/components/ui/select'
import { useEffect, useRef, useState } from 'react'
import type { Condition, ConditionField, MatchMode } from './condition-filter-url'
import { OPERATORS } from './condition-filter-url'
import { ConditionValueInput } from './condition-value-input'

type DraftCondition = Condition & { rowKey: number }

export function ConditionEditor({
  fields,
  conditions,
  match,
  onApply,
  onCancel,
  errors = {},
  companyId = null,
  focusIndex,
}: {
  fields: ConditionField[]
  conditions: Condition[]
  match: MatchMode
  onApply: (conditions: Condition[], match: MatchMode) => void
  onCancel: () => void
  errors?: Record<string, string>
  companyId?: string | null
  focusIndex?: number | null
}) {
  const { t } = useTranslation('condition-filter')
  const rowSequence = useRef(0)
  const newDraft = (condition: Condition): DraftCondition => ({
    ...condition,
    rowKey: ++rowSequence.current,
  })
  const [draft, setDraft] = useState<DraftCondition[]>(() =>
    (conditions.length ? conditions : [{ field: fields[0]?.key ?? '', op: '', value: '' }]).map(
      newDraft,
    ),
  )
  const [draftMatch, setDraftMatch] = useState(match)
  useEffect(() => {
    if (focusIndex === undefined || focusIndex === null || focusIndex >= draft.length) return
    document
      .querySelector<HTMLElement>(`[data-condition-row="${focusIndex}"] [role="combobox"]`)
      ?.focus()
  }, [focusIndex, draft])
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onCancel])

  const patch = (index: number, changes: Partial<Condition>) =>
    setDraft((current) =>
      current.map((item, row) => (row === index ? { ...item, ...changes } : item)),
    )
  const apply = () => {
    onApply(
      draft
        .filter((item) => item.field && item.op && valid(item))
        .map(({ rowKey: _rowKey, ...condition }) => condition),
      draftMatch,
    )
  }

  return (
    <div className="grid gap-3" data-condition-editor>
      {draft.map((condition, index) => {
        const field = fields.find((item) => item.key === condition.field)
        const ops = field ? OPERATORS[field.type] : []
        const rowError =
          errors[`conditions.${index}.field`] ??
          errors[`conditions.${index}.op`] ??
          errors[`conditions.${index}.value`]
        return (
          <div
            key={condition.rowKey}
            className="grid min-w-0 items-end gap-2 sm:grid-cols-[1fr_1fr_2fr_auto]"
            data-condition-row={index}
          >
            <div className="grid gap-1 text-small font-medium">
              <span>{t('field')}</span>
              <Select
                value={condition.field}
                onValueChange={(value) => {
                  const next = fields.find((item) => item.key === value)
                  patch(index, {
                    field: value,
                    op: next ? OPERATORS[next.type][0] : '',
                    value: undefined,
                  })
                }}
              >
                <SelectTrigger aria-label={t('field')} className="w-full min-w-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {fields.map((item) => (
                    <SelectItem key={item.key} value={item.key}>
                      {t(`fields.${item.key}`, { defaultValue: item.key })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1 text-small font-medium">
              <span>{t('operator')}</span>
              <Select
                value={condition.op}
                onValueChange={(op) =>
                  patch(index, {
                    op,
                    value:
                      op === 'empty' ||
                      op === 'not_empty' ||
                      ['this_month', 'last_month', 'this_quarter', 'this_year'].includes(op)
                        ? undefined
                        : '',
                  })
                }
              >
                <SelectTrigger aria-label={t('operator')} className="w-full min-w-0">
                  <SelectValue placeholder={t('choose')} />
                </SelectTrigger>
                <SelectContent>
                  {ops.map((op) => (
                    <SelectItem key={op} value={op}>
                      {t(`operators.${op}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <ConditionValueInput
              field={field}
              condition={condition}
              companyId={companyId}
              onChange={(value) => patch(index, { value })}
            />
            <Button
              type="button"
              variant="ghost"
              aria-label={t('remove')}
              disabled={draft.length === 1}
              onClick={() => setDraft((rows) => rows.filter((_, row) => row !== index))}
            >
              ×
            </Button>
            {rowError ? (
              <p role="alert" className="text-small text-danger sm:col-span-4">
                {rowError}
              </p>
            ) : null}
          </div>
        )
      })}
      <Button
        type="button"
        variant="ghost"
        className="self-start"
        disabled={draft.length >= 10}
        onClick={() =>
          setDraft((rows) => [
            ...rows,
            newDraft({ field: fields[0]?.key ?? '', op: '', value: '' }),
          ])
        }
      >
        {t('add')}
      </Button>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SegmentedControl
          ariaLabel={t('match')}
          value={draftMatch}
          onValueChange={setDraftMatch}
          options={(['all', 'any'] as const).map((value) => ({
            value,
            label: t(`matchMode.${value}`),
          }))}
        />
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            {t('cancel')}
          </Button>
          <Button type="button" data-condition-apply onClick={apply}>
            {t('apply')}
          </Button>
        </div>
      </div>
    </div>
  )
}

function valid(condition: Condition) {
  if (
    condition.op === 'empty' ||
    condition.op === 'not_empty' ||
    ['this_month', 'last_month', 'this_quarter', 'this_year'].includes(condition.op)
  )
    return true
  if (condition.op === 'between')
    return (
      Array.isArray(condition.value) &&
      condition.value.length === 2 &&
      condition.value.every(Boolean)
    )
  if (condition.op === 'any_of' || condition.op === 'none_of')
    return Array.isArray(condition.value) && condition.value.length > 0
  return typeof condition.value === 'string' && condition.value !== ''
}
